import React from 'react';
import Modal from '@common/components/Modal';
import SpinnerContainer from '@common/components/SpinnerContainer';
import userPersonas from '@common/constants/userPersonas';
import SelectorPage from './SelectorPage';
import WhatsNextPage from './WhatsNextPage';
import RequestPage from './RequestPage';
import pageFlows from '@rental-manager/pages/Dashboard/OneTimePaymentModalV2/pageFlows';
import type { OneTimePaymentRequestInterface } from '@rental-manager/pages/Dashboard/types';

const DEFAULT_TENANT_INDEX = 0;

type Props = {
  rentTermsKeyArray: Array<string>;
  tenantNameArray: Array<string>;
  oneTimePaymentLimitsArray: Array<Record<string, unknown>>;
  oneTimePaymentRequest: OneTimePaymentRequestInterface;
  onSubmit: (args: OneTimePaymentRequestInterface) => Record<string, any>;
  onClose: () => void;
  goToDashboard: (tenantFirstName: string) => void;
  paymentPlanEnabled: boolean;
  onNextFromSelectorPage: (selectedOption: string) => any;
  selectedOption: string;
  goToSetupPaymentPlan: () => void;
};

type State = {
  isLoading: boolean;
  selectedTenantIndex: number;
  page: string;
};
class OneTimePaymentModal extends React.Component<Props, State> {
  static defaultProps = {
    oneTimePaymentRequest: {
      amount: '',
      type: '',
      notes: '',
      rentTermsKey: '',
    },
  };

  constructor(props: Props) {
    super(props);
    const firstPopulatedOneTimePaymentLimit = props.oneTimePaymentLimitsArray.findIndex(
      (otpLimits) => Object.keys(otpLimits).length > 0,
    );
    this.state = {
      isLoading: false,
      selectedTenantIndex:
        firstPopulatedOneTimePaymentLimit > -1 ? firstPopulatedOneTimePaymentLimit : DEFAULT_TENANT_INDEX,
      page: props.paymentPlanEnabled ? pageFlows.SelectorPage : pageFlows.OneTimePaymentRequestInputPage,
    };
  }

  showLoadingScreen = (): void => {
    this.setState({ isLoading: true });
  };

  hideLoadingScreen = (): void => {
    this.setState({ isLoading: false });
  };

  handleTenantChange = (index: number): void => {
    this.setState({
      selectedTenantIndex: index,
    });
  };

  onSubmit = async (payload): Promise<void> => {
    this.showLoadingScreen();
    const response = await this.props.onSubmit(payload);
    this.hideLoadingScreen();

    if (response.error) {
      return;
    }

    this.setState({ page: pageFlows.SuccessPage });
  };

  onClose = (): void => {
    if (this.state.page === pageFlows.SuccessPage) {
      this.goToDashboard();
    } else {
      this.props.onClose();
    }
  };

  goToDashboard = (): void => {
    const { tenantNameArray } = this.props;
    this.props.goToDashboard(tenantNameArray[this.state.selectedTenantIndex]);
  };

  onNextFromSelectorPage = (selectedOption: string): any => {
    if (selectedOption === 'Payment Plan') {
      this.props.goToSetupPaymentPlan();
    } else {
      this.setState({
        page: pageFlows.OneTimePaymentRequestInputPage,
      });
    }
  };

  renderPage = () => {
    if (this.state.page === pageFlows.SuccessPage) {
      return <WhatsNextPage goToDashboard={this.goToDashboard} />;
    } else if (this.state.page === pageFlows.SelectorPage) {
      return <SelectorPage onCancel={this.props.onClose} onNextFromSelectorPage={this.onNextFromSelectorPage} />;
    } else {
      return (
        <RequestPage
          selectedTenantIndex={this.state.selectedTenantIndex}
          handleTenantChange={this.handleTenantChange}
          rentTermsKeyArray={this.props.rentTermsKeyArray}
          tenantNameArray={this.props.tenantNameArray}
          oneTimePaymentLimitsArray={this.props.oneTimePaymentLimitsArray}
          onSubmit={this.onSubmit}
          onCancel={this.onClose}
        />
      );
    }
  };

  render() {
    const { isLoading } = this.state;
    return (
      <Modal
        persona={userPersonas.LANDLORD}
        header="Payment Request"
        onRequestClose={this.onClose}
        dataAutoTestId="landlord-one-time-payment"
      >
        {isLoading && <SpinnerContainer />}
        {this.renderPage()}
      </Modal>
    );
  }
}

export default OneTimePaymentModal;
